import config from 'app/config'
import makeSiteGraphqlRequest from 'app/lib/graphql-request'
import { useStores } from 'app/store'
import { Pagination } from 'app/types/pagination'
import { useInfiniteQuery } from 'react-query'
import { logError } from 'app/lib/log-error'

interface Base {
  id: string
}

interface Options<T> {
  limit?: number
  formatFn: (data: any) => Pagination<T>
  enabled?: boolean
  queryKey?: string
  keepPreviousData?: boolean
}

export async function makeQuery<T>(
  query: string,
  variables: any,
  page: number,
  limit: number,
  formatFn?: (data: any) => Pagination<T>,
  userStore?: any,
) {
  try {
    return await makeSiteGraphqlRequest(
      config,
      query,
      { ...variables, page, limit },
      { userStore, formatFn },
    )
  } catch (e) {
    console.error(e)

    logError(e, query, variables)
  }
}

export const getNextPageParam = (lastPage, allPages, limit) => {
  if (!lastPage) return null

  const total = lastPage?.total || 0
  const totalFetched = allPages.reduce((sum, page) => sum + (page.docs?.length || 0), 0)
  if (totalFetched < total) {
    return lastPage?.page + 1
  }
}

export default function usePagination<T extends Base, P extends Pagination<T>>(
  query: string,
  variables: any,
  options: Options<T>,
) {
  const { userStore } = useStores()
  const limit = options.limit || 12
  const enabled = options.hasOwnProperty('enabled') ? options.enabled : true

  // Check if we have a sort param and if so, add it to the query
  if (query.includes('$sort')) {
    variables.sort = variables.sort || '-order'
  }

  async function fetchData({ pageParam = 1 }) {
    if (!userStore) return

    const page = pageParam ?? 1
    return makeQuery(query, variables, page, limit, options.formatFn, userStore)
  }

  const {
    data,
    isLoading,
    isFetchingNextPage,
    isFetching,
    error,
    fetchNextPage,
    isRefetching,
    hasNextPage,
    refetch,
    isFetchedAfterMount,
    isPreviousData,
  } = useInfiniteQuery(
    [options?.queryKey || 'pagination', query, variables],
    fetchData,
    {
      enabled: !!userStore && enabled,
      keepPreviousData: options.keepPreviousData ?? false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = getNextPageParam(lastPage, allPages, limit)
        return nextPage
      },
    },
  )

  const concatData = data?.pages?.reduce(
    (acc, page) => {
      if (!page) return acc

      const data = [...acc.docs, ...page.docs]

      return {
        ...page,
        docs: data,
      }
    },
    { docs: [], total: 0 },
  )

  return {
    data: concatData as P,
    loading: isLoading,
    isFetching,
    isFetchingNextPage,
    isPreviousData,
    error: error as Error,
    hasMore: hasNextPage,
    refetch,
    refetching: isRefetching,
    fetchData: () => {
      return refetch()
    },
    fetchMore: () => {
      return fetchNextPage()
    },
    isFetchedAfterMount,
  }
}
